import { useState } from 'react';
import { useWebSocket } from '../context/WebSocketContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Bell,
  MessageSquare,
  Package,
  User,
  Calendar,
  FileText,
  Vote,
  Wrench,
  CreditCard,
  Wifi,
  WifiOff,
  X,
  Check,
} from 'lucide-react';

const notificationIcons = {
  new_message: MessageSquare,
  message_reply: MessageSquare,
  message_like: MessageSquare,
  visitor_registered: User,
  visitor_checked_in: User,
  visitor_checked_out: User,
  parcel_received: Package,
  parcel_collected: Package,
  new_notice: FileText,
  notice_updated: FileText,
  ticket_created: Wrench,
  ticket_updated: Wrench,
  ticket_resolved: Wrench,
  new_poll: Vote,
  poll_vote_cast: Vote,
  poll_closed: Vote,
  booking_confirmed: Calendar,
  booking_cancelled: Calendar,
  invoice_created: CreditCard,
  payment_received: CreditCard,
  new_event: Calendar,
  event_rsvp: Calendar,
  event_reminder: Calendar,
};

const notificationColors = {
  new_message: 'bg-sky-100 text-sky-600',
  message_reply: 'bg-sky-100 text-sky-600',
  visitor_checked_in: 'bg-emerald-100 text-emerald-600',
  parcel_received: 'bg-amber-100 text-amber-600',
  new_notice: 'bg-purple-100 text-purple-600',
  ticket_created: 'bg-orange-100 text-orange-600',
  ticket_resolved: 'bg-emerald-100 text-emerald-600',
  new_poll: 'bg-indigo-100 text-indigo-600',
  booking_confirmed: 'bg-green-100 text-green-600',
  payment_received: 'bg-emerald-100 text-emerald-600',
  new_event: 'bg-pink-100 text-pink-600',
};

export default function NotificationBell() {
  const { isConnected, notifications, unreadCount, clearNotifications, markAsRead } = useWebSocket();
  const [open, setOpen] = useState(false);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleOpen = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      markAsRead();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          {/* Connection indicator */}
          <span className={`absolute bottom-0 right-0 w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <CardHeader className="p-4 border-b flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">Notifications</CardTitle>
            {isConnected ? (
              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                <Wifi className="w-3 h-3 mr-1" />
                Live
              </Badge>
            ) : (
              <Badge className="bg-slate-100 text-slate-500 text-xs">
                <WifiOff className="w-3 h-3 mr-1" />
                Offline
              </Badge>
            )}
          </div>
          {notifications.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs text-slate-500"
              onClick={clearNotifications}
            >
              Clear all
            </Button>
          )}
        </CardHeader>
        <ScrollArea className="h-80">
          {notifications.length > 0 ? (
            <div className="divide-y">
              {notifications.map((notification, index) => {
                const Icon = notificationIcons[notification.type] || Bell;
                const colorClass = notificationColors[notification.type] || 'bg-slate-100 text-slate-600';
                
                return (
                  <div 
                    key={index} 
                    className="p-3 hover:bg-slate-50 transition-colors cursor-pointer"
                    data-testid={`notification-${index}`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-900 truncate">{notification.title}</p>
                        <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{notification.message}</p>
                        <p className="text-xs text-slate-400 mt-1">{formatTime(notification.timestamp)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Bell className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No notifications yet</p>
              <p className="text-xs text-slate-400 mt-1">
                {isConnected 
                  ? "We'll notify you of new activity" 
                  : "Connecting to live updates..."}
              </p>
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
